import { currentBreakpoint } from "@otto-ec/global-resources/viewport";
import { DirectDresonRuleResolveEvent, DresonRuleResolveEvent } from "../../types/FilterTypes";
import { eventQBus } from "../../types/EventQBus";
import { createProductionSelectionDependencies } from "./SelectionDependencies";

export default class FilterResolveDresonRule {
  public static async resolve(event: DresonRuleResolveEvent | DirectDresonRuleResolveEvent) {
    if (event.redirect) {
      await FilterResolveDresonRule.assign(event).catch(FilterResolveDresonRule.handleError);
      return;
    }

    if (event.historic) {
      FilterResolveDresonRule.handleError();
      return;
    }

    try {
      await FilterResolveDresonRule.resolveRule(event).catch(FilterResolveDresonRule.handleError);
    } catch {
      FilterResolveDresonRule.handleError();
    }
  }

  private static async assign(event: DirectDresonRuleResolveEvent): Promise<void> {
    FilterResolveDresonRule.validateRule(event.rule);
    const dependencies = createProductionSelectionDependencies();
    const response = await dependencies.resolver(new URLSearchParams({ rule: event.rule }), 0);
    dependencies.dom.applyResolvedResponse(response);

    eventQBus.emit("ftfind.dresonRule.resolved", {
      ...event,
      mergeOnPi: true,
      ...(response.count !== undefined ? { count: response.count } : {}),
      entryRule: event.entryRule || dependencies.dom.historyEntryRule(),
      ssid: FilterResolveDresonRule.ssid(dependencies.dom.currentSsid()),
    });
  }

  private static async resolveRule(event: DresonRuleResolveEvent | DirectDresonRuleResolveEvent): Promise<void> {
    const input = FilterResolveDresonRule.resolveInput(event);
    const dependencies = createProductionSelectionDependencies();
    const params = new URLSearchParams();
    Object.entries(input).forEach(([key, value]) =>
      typeof value === "string" ? params.append(key, value) : value.forEach((entry) => params.append(key, entry)),
    );

    const response = await dependencies.resolver(params, 0);
    dependencies.dom.applyResolvedResponse(response);
    const breakpoint = await currentBreakpoint.get();

    eventQBus.emit("ftfind.dresonRule.resolved", {
      mergeOnPi: true,
      rule: response.rule,
      page: {
        ...(typeof input.l === "string" ? { l: input.l } : {}),
        ...(typeof input.o === "string" ? { o: input.o } : {}),
        ...(typeof input.sortiertnach === "string" ? { sortiertnach: input.sortiertnach } : {}),
      },
      redirect: FilterResolveDresonRule.legacyUrl(response.url),
      shouldScroll: breakpoint === "s" || breakpoint === "m",
      ...(response.count !== undefined ? { count: response.count } : {}),
      entryRule: event.entryRule || dependencies.dom.historyEntryRule(),
      ssid: FilterResolveDresonRule.ssid(dependencies.dom.currentSsid()),
    });
  }

  private static resolveInput(
    event: DresonRuleResolveEvent | DirectDresonRuleResolveEvent,
  ): Record<string, string | string[]> {
    const input: Record<string, string | string[]> = {};
    if (event.formData) {
      for (const [key, value] of event.formData) {
        if (typeof value !== "string") {
          throw new Error(`Unsupported selection field "${key}"`);
        }

        const previous = input[key];
        input[key] =
          previous === undefined ? value : Array.isArray(previous) ? [...previous, value] : [previous, value];
      }
    }

    const directRule = "rule" in event ? event.rule : undefined;
    if (input.rule === undefined && directRule !== undefined) input.rule = directRule;

    const pageSort = event.page?.sortiertnach;
    const formSort = input.sortiertnach;
    if (pageSort !== undefined && formSort !== undefined && !FilterResolveDresonRule.hasOnlyValue(formSort, pageSort)) {
      throw new Error("Conflicting sortiertnach values");
    }
    if (pageSort !== undefined && formSort === undefined) input.sortiertnach = pageSort;

    if (typeof input.rule !== "string") {
      throw new Error("Invalid rule");
    }
    FilterResolveDresonRule.validateRule(input.rule);

    return input;
  }

  private static hasOnlyValue(value: string | string[], expected: string): boolean {
    return typeof value === "string" ? value === expected : value.every((entry) => entry === expected);
  }

  private static ssid(ssid: string | undefined): string {
    if (!ssid) throw new Error("Failed to resolve rule: missing ssid");
    return ssid;
  }

  private static legacyUrl(url: string): string {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  }

  private static validateRule(rule: unknown): asserts rule is string {
    if (typeof rule !== "string" || !rule || rule === "undefined" || rule === "null") throw new Error("Invalid rule");
  }

  private static handleError(): void {
    eventQBus.emit("ftfind.dresonRule.resolveAborted");
  }
}
